import {
  useCallback,
} from "react";
import { useInterfaceBuilderAsyncRunner } from "@/components/Layout/account/useInterfaceBuilderAsyncRunner";
import type { InterfaceBuilderSelection } from "@/contexts/UserContext";
import {
  mergeUpdatedComponent,
  moveComponentInAncestorSchemes,
  resolveSelectedComponent,
} from "@/interfaceBuilder/componentSchemeTree";
import type {
  BuilderProperty,
  BuilderPropertyValue,
} from "@/interfaceBuilder/propertyDefinitions";
import {
  updateUiComponent,
  updateUiSidebarItem,
} from "@/services/uiApi";
import type { StoredUiComponent } from "@/storage/uiComponentsRepository";
import {
  upsertUiSidebarItem,
  type StoredUiSidebarItem,
} from "@/storage/uiSidebarItemsRepository";

interface InterfaceBuilderPropertyActionParams {
  components: readonly StoredUiComponent[];
  isAuthenticated: boolean;
  projectId?: string | null;
  sidebarItems: readonly StoredUiSidebarItem[];
  userId?: string | null;
}

interface BuilderPropertyChange {
  property: BuilderProperty;
  selection: InterfaceBuilderSelection;
  value: BuilderPropertyValue;
}

interface ResolvedComponentPropertyChange extends BuilderPropertyChange {
  component: StoredUiComponent;
  selection: Extract<InterfaceBuilderSelection, { kind: "component" }>;
}

interface ResolvedSidebarItemPropertyChange extends BuilderPropertyChange {
  sidebarItem: StoredUiSidebarItem;
  selection: Extract<InterfaceBuilderSelection, { kind: "sidebarItem" }>;
}

export function useInterfaceBuilderPropertyActions({
  components,
  isAuthenticated,
  projectId,
  sidebarItems,
  userId,
}: InterfaceBuilderPropertyActionParams) {
  const {
    clearError: clearEditError,
    error: editError,
    errorDetails: editErrorDetails,
    run: runEdit,
    submitting,
  } = useInterfaceBuilderAsyncRunner({
    errorFallbackKey: "interfaceBuilderInspector.editErrorMessage",
    isAuthenticated,
    projectId,
    userId,
  });

  const commitPropertyChanges = useCallback(async (
    changes: readonly BuilderPropertyChange[],
  ) => {
    const editableChanges = changes.filter((change) => change.property.editable);
    if (editableChanges.length === 0) return false;

    return runEdit(async (signal, scopeKey) => {
      const componentChanges = resolveComponentPropertyChanges(
        {
          changes: editableChanges,
          components,
        },
      );
      const sidebarItemChanges = resolveSidebarItemPropertyChanges(
        sidebarItems,
        editableChanges,
      );
      if (
        componentChanges.length + sidebarItemChanges.length
        !== editableChanges.length
      ) {
        return false;
      }

      let workingComponents = components;
      for (const group of groupComponentPropertyChanges(componentChanges)) {
        const currentComponent = workingComponents.find(
          (component) => component.uuid === group.component.uuid,
        ) ?? group.component;
        const changedComponents = await commitComponentPropertyChanges({
          changes: group.changes,
          component: currentComponent,
          components: workingComponents,
          scopeKey,
          signal,
        });
        if (!changedComponents) return false;

        workingComponents = replaceComponents(
          workingComponents,
          changedComponents,
        );
      }

      for (const group of groupSidebarItemPropertyChanges(sidebarItemChanges)) {
        const succeeded = await commitSidebarItemPropertyChanges({
          changes: group.changes,
          scopeKey,
          sidebarItem: group.sidebarItem,
          signal,
        });
        if (!succeeded) return false;
      }

      return true;
    });
  }, [
    components,
    runEdit,
    sidebarItems,
  ]);

  const commitPropertyChange = useCallback(async ({
    property,
    selection,
    value,
  }: BuilderPropertyChange) => {
    return commitPropertyChanges([{
      property,
      selection,
      value,
    }]);
  }, [commitPropertyChanges]);

  return {
    clearEditError,
    commitPropertyChange,
    commitPropertyChanges,
    editError,
    editErrorDetails,
    submitting,
  };
}

function resolveComponentPropertyChanges({
  components,
  changes,
}: {
  components: readonly StoredUiComponent[];
  changes: readonly BuilderPropertyChange[];
}): ResolvedComponentPropertyChange[] {
  return changes.flatMap((change) => {
    if (change.selection.kind !== "component") return [];

    const component = resolveSelectedComponent(components, change.selection);
    if (!component) return [];

    return [{
      ...change,
      component,
      selection: change.selection,
    }];
  });
}

function resolveSidebarItemPropertyChanges(
  sidebarItems: readonly StoredUiSidebarItem[],
  changes: readonly BuilderPropertyChange[],
): ResolvedSidebarItemPropertyChange[] {
  return changes.flatMap((change) => {
    if (change.selection.kind !== "sidebarItem") return [];

    const { selection } = change;
    const sidebarItem = sidebarItems.find(
      (item) => item.uuid === selection.sidebarItemId,
    );
    if (!sidebarItem) return [];

    return [{
      ...change,
      sidebarItem,
      selection,
    }];
  });
}

function groupComponentPropertyChanges(
  changes: readonly ResolvedComponentPropertyChange[],
): Array<{
  changes: readonly ResolvedComponentPropertyChange[];
  component: StoredUiComponent;
}> {
  const groups = new Map<string, {
    changes: ResolvedComponentPropertyChange[];
    component: StoredUiComponent;
  }>();

  changes.forEach((change) => {
    const group = groups.get(change.component.uuid);
    if (group) {
      group.changes.push(change);
      return;
    }

    groups.set(change.component.uuid, {
      changes: [change],
      component: change.component,
    });
  });

  return [...groups.values()];
}

function groupSidebarItemPropertyChanges(
  changes: readonly ResolvedSidebarItemPropertyChange[],
): Array<{
  changes: readonly ResolvedSidebarItemPropertyChange[];
  sidebarItem: StoredUiSidebarItem;
}> {
  const groups = new Map<string, {
    changes: ResolvedSidebarItemPropertyChange[];
    sidebarItem: StoredUiSidebarItem;
  }>();

  changes.forEach((change) => {
    const group = groups.get(change.sidebarItem.uuid);
    if (group) {
      group.changes.push(change);
      return;
    }

    groups.set(change.sidebarItem.uuid, {
      changes: [change],
      sidebarItem: change.sidebarItem,
    });
  });

  return [...groups.values()];
}

async function commitComponentPropertyChanges({
  changes,
  component,
  components,
  scopeKey,
  signal,
}: {
  changes: readonly ResolvedComponentPropertyChange[];
  component: StoredUiComponent;
  components: readonly StoredUiComponent[];
  scopeKey: string;
  signal: AbortSignal;
}): Promise<readonly StoredUiComponent[] | null> {
  if (changes.length === 0) return null;

  const props = changes.reduce<Record<string, unknown>>(
    (draftProps, change) => readEntityPropsPatch(
      draftProps,
      change.property.path,
      change.value,
    ),
    readComponentProps(component),
  );

  const savedComponent = await updateUiComponent(
    component.uuid,
    { props },
    { signal },
  );

  const mergedComponent = mergeUpdatedComponent(component, savedComponent);

  return moveComponentInAncestorSchemes(
    scopeKey,
    components,
    mergedComponent,
    component.parentUuid ?? null,
  );
}

function replaceComponents(
  components: readonly StoredUiComponent[],
  changedComponents: readonly StoredUiComponent[],
): readonly StoredUiComponent[] {
  if (changedComponents.length === 0) return components;

  const changedByUuid = new Map(
    changedComponents.map((component) => [component.uuid, component]),
  );
  const seen = new Set<string>();
  const nextComponents = components.map((component) => {
    const changed = changedByUuid.get(component.uuid);
    if (!changed) return component;

    seen.add(component.uuid);
    return changed;
  });

  changedComponents.forEach((component) => {
    if (!seen.has(component.uuid)) {
      nextComponents.push(component);
    }
  });

  return nextComponents;
}

async function commitSidebarItemPropertyChanges({
  changes,
  scopeKey,
  sidebarItem,
  signal,
}: {
  changes: readonly ResolvedSidebarItemPropertyChange[];
  scopeKey: string;
  sidebarItem: StoredUiSidebarItem;
  signal: AbortSignal;
}): Promise<boolean> {
  if (changes.length === 0) return false;

  const props = changes.reduce<Record<string, unknown>>(
    (draftProps, change) => readEntityPropsPatch(
      draftProps,
      change.property.path,
      change.value,
    ),
    sidebarItem.props ?? {},
  );

  const savedItem = await updateUiSidebarItem(
    sidebarItem.uuid,
    { props },
    { signal },
  );

  await upsertUiSidebarItem(scopeKey, savedItem);
  return true;
}

function readComponentProps(
  component: StoredUiComponent,
): Record<string, unknown> {
  return component.props
    ?? component.uiScheme;
}

function readEntityPropsPatch(
  props: Record<string, unknown> | undefined,
  propertyPath: readonly string[],
  value: BuilderPropertyValue,
): Record<string, unknown> {
  return writeNestedProperty(props ?? {}, propertyPath, value);
}

function writeNestedProperty(
  source: Record<string, unknown>,
  path: readonly string[],
  value: BuilderPropertyValue,
): Record<string, unknown> {
  if (path.length === 0) return source;

  const [head, ...tail] = path;
  if (tail.length === 0) {
    return {
      ...source,
      [head]: value,
    };
  }

  const currentValue = source[head];
  const currentRecord = isRecord(currentValue) ? currentValue : {};

  return {
    ...source,
    [head]: writeNestedProperty(currentRecord, tail, value),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
